import React, { useEffect, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { SpeedToLeadTouchpointPreviews } from 'types/recommendations';

import { SPEED_TO_LEAD_FOLLOW_UP_DAYS, SPEED_TO_LEAD_FOLLOW_UP_VIEW } from './constants';
import { getEventChannel } from './speedToLeadOverallUtils';
import SpeedToLeadTouchpoint from './SpeedToLeadTouchpoint';
import { TouchpointPreviewSelection } from './SpeedToLeadTouchpointPreviewModal.types';
import { formatTouchCount, getTouchpointKey } from './touchpointUtils';

const DayTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 0 0 16px;
`;

const DayTab = styled.button<{ active: boolean }>`
  display: inline-flex;
  height: 24px;
  align-items: center;
  padding: 0 10px;
  border: 1px solid ${({ active }) => (active ? 'var(--button-blue)' : 'var(--lighter-gray)')};
  border-radius: 12px;
  background: ${({ active }) => (active ? 'var(--button-blue)' : 'var(--white)')};
  box-shadow: 0 2px 4px 0 var(--box-shadow-gray-dark);
  color: ${({ active }) => (active ? 'var(--white)' : 'var(--text-primary)')};
  cursor: pointer;
  font-family: var(--font-regular);
  font-size: 10px;

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const TabCount = styled.span`
  margin-left: 4px;
  color: inherit;
  font-size: 9px;
  opacity: 0.65;
`;

const DaySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`;

const DayHeading = styled.button`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font-family: var(--font-bold);
  font-size: 14px;

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const DayChevron = styled.span<{ expanded: boolean }>`
  display: inline-flex;
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0deg')});
  transition: transform 120ms ease;
`;

const EmptyState = styled.div`
  padding: 28px 0;
  color: var(--text-secondary);
  font-size: 13px;
  text-align: center;
`;

const LoadingState = styled(EmptyState)`
  color: var(--text-primary);
`;

type Props = {
  data: SpeedToLeadTouchpointPreviews | undefined;
  isLoading: boolean;
  isError: boolean;
  selection: TouchpointPreviewSelection;
};

const SpeedToLeadTouchpointPreviewModalContent = ({ data, isLoading, isError, selection }: Props) => {
  const [activeDay, setActiveDay] = useState<number | null>(selection.day ?? null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(
    () => new Set(selection.day == null ? [] : [selection.day])
  );
  const selectedTouchpointRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!data || selection.activityId == null) {
      return undefined;
    }

    const animationFrame = requestAnimationFrame(() => {
      selectedTouchpointRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [data, selection.activityId]);
  const chronologicalEvents = useMemo(
    () =>
      [...(data?.events ?? [])].sort(
        (left, right) => new Date(left.occurred_at).getTime() - new Date(right.occurred_at).getTime()
      ),
    [data?.events]
  );
  const channelEvents = chronologicalEvents.filter(
    (event) => !selection.channel || getEventChannel(event) === selection.channel
  );
  const visibleEvents =
    selection.mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL
      ? channelEvents
      : chronologicalEvents.filter((event) => activeDay == null || event.day === activeDay);
  const visibleDays = Array.from(new Set(visibleEvents.map((event) => event.day))).sort((left, right) => left - right);
  const openDay = (day: number) => {
    setActiveDay(day);
    setExpandedDays((currentDays) => new Set(currentDays).add(day));
  };

  const toggleDay = (day: number) => {
    setExpandedDays((currentDays) => {
      const nextDays = new Set(currentDays);
      if (nextDays.has(day)) {
        nextDays.delete(day);
      } else {
        nextDays.add(day);
      }
      return nextDays;
    });
  };

  return (
    <>
      {selection.mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.SEQUENCE && (
        <DayTabs>
          <DayTab
            type="button"
            active={activeDay == null}
            onClick={() => setActiveDay(null)}
            data-test-id="speed-to-lead-touchpoint-day-all"
          >
            All<TabCount>{formatTouchCount(chronologicalEvents.length)}</TabCount>
          </DayTab>
          {SPEED_TO_LEAD_FOLLOW_UP_DAYS.map((day) => {
            const count = chronologicalEvents.filter((event) => event.day === day).length;
            return (
              <DayTab
                type="button"
                key={day}
                active={activeDay === day}
                onClick={() => openDay(day)}
                data-test-id={`speed-to-lead-touchpoint-day-${day}`}
              >
                Day {day}
                <TabCount>{formatTouchCount(count)}</TabCount>
              </DayTab>
            );
          })}
        </DayTabs>
      )}
      {isLoading && <LoadingState>Loading touchpoint details…</LoadingState>}
      {isError && <EmptyState>Touchpoint details could not be loaded.</EmptyState>}
      {!isLoading && !isError && visibleEvents.length === 0 && (
        <EmptyState>No touchpoints for this selection.</EmptyState>
      )}
      {!isLoading && !isError && selection.mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL && (
        <DaySection>
          {visibleEvents.map((event) => (
            <SpeedToLeadTouchpoint
              key={getTouchpointKey(event, chronologicalEvents)}
              event={event}
              eventKey={getTouchpointKey(event, chronologicalEvents)}
              events={chronologicalEvents}
              mode={selection.mode}
              defaultExpanded
            />
          ))}
        </DaySection>
      )}
      {!isLoading &&
        !isError &&
        selection.mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.SEQUENCE &&
        visibleDays.map((day) => {
          const dayEvents = visibleEvents.filter((event) => event.day === day);
          const isExpanded = expandedDays.has(day);
          return (
            <DaySection key={day}>
              <DayHeading
                type="button"
                onClick={() => toggleDay(day)}
                aria-expanded={isExpanded}
                data-test-id={`speed-to-lead-touchpoint-day-toggle-${day}`}
              >
                <DayChevron expanded={isExpanded}>
                  <ChevronRight fill="var(--text-secondary)" width={16} height={16} />
                </DayChevron>
                Day {day} ({formatTouchCount(dayEvents.length)})
              </DayHeading>
              {isExpanded &&
                dayEvents.map((event) => (
                  <SpeedToLeadTouchpoint
                    key={getTouchpointKey(event, chronologicalEvents)}
                    event={event}
                    eventKey={getTouchpointKey(event, chronologicalEvents)}
                    events={chronologicalEvents}
                    mode={selection.mode}
                    defaultExpanded
                    selected={selection.activityId != null && event.activity_id === selection.activityId}
                    cardRef={
                      selection.activityId != null && event.activity_id === selection.activityId
                        ? selectedTouchpointRef
                        : undefined
                    }
                  />
                ))}
            </DaySection>
          );
        })}
    </>
  );
};

export default SpeedToLeadTouchpointPreviewModalContent;
